/**
 * Channel refs for chart axes and channel-values API (name + optional stream).
 * Output-key rules must match server_file/middleware/channel_stream_output.js.
 */

import { stripFleetChannelSuffix } from './timeseriesSeriesTransforms';

export type ChannelRefDataSource = 'file' | 'influx';

/** Catalog entry shape for resolving chart refs to parquet/Influx names. */
export interface CatalogChannelLike {
  channel_name: string;
  stream_name?: string;
}

/** Lowercase match key: trims, lowercases, maps dots to underscores (Influx vs parquet naming). */
export function normalizeChannelNameForMatch(name: string): string {
  return String(name ?? '').trim().toLowerCase().replace(/\./g, '_');
}

/** Chart/stream name → parquet column sent to channel-values API. */
export function resolveParquetColumnName(name: string): string {
  return chartNameToParquetName(name);
}

/** Whether a normalized data key satisfies a requested channel (dot/underscore only; no semantic substitution). */
export function channelInNormalizedSet(channel: string, normalizedSet: Set<string>): boolean {
  return normalizedSet.has(normalizeChannelNameForMatch(channel));
}

/** Map chart/Influx dotted names to parquet column names (preserve case). */
export function chartNameToParquetName(name: string): string {
  return String(name ?? '').trim().replace(/\./g, '_');
}

export function channelNamesMatchForDiscovery(a: string, b: string): boolean {
  return normalizeChannelNameForMatch(a) === normalizeChannelNameForMatch(b);
}

export interface ChannelRef {
  name: string;
  stream?: string;
  /** Per-channel source preference for channel-values (omit = auto / FILE-first). */
  data_source?: ChannelRefDataSource;
}

export interface AxisChannelLike {
  name?: string;
  stream?: string;
  data_source?: string;
}

export function normalizeAxisDataSource(raw?: string | null): ChannelRefDataSource | undefined {
  const v = String(raw ?? '').trim().toLowerCase();
  if (v === 'influx') {return 'influx';}
  if (v === 'file') {return 'file';}
  return undefined;
}

export function normalizeStreamKey(stream: string | null | undefined): string {
  const t = String(stream ?? '').trim();
  return t.length ? t.toLowerCase() : '';
}

export function streamOutputKeySuffix(stream: string | null | undefined): string {
  const norm = normalizeStreamKey(stream);
  return norm ? `__stream__${norm}` : '';
}

export function buildStreamScopedOutputKey(
  name: string,
  stream: string | null | undefined,
  needsDisambiguation: boolean
): string {
  const suffix = streamOutputKeySuffix(stream);
  if (!needsDisambiguation || !suffix) {return name;}
  return `${name}${suffix}`;
}

function distinctNonemptyStreamCountByName(refs: ChannelRef[]): Map<string, number> {
  const byName = new Map<string, Set<string>>();
  for (const ref of refs) {
    if (!ref?.name) {continue;}
    const low = normalizeChannelNameForMatch(ref.name);
    const streamNorm = normalizeStreamKey(ref.stream);
    if (!streamNorm) {continue;}
    if (!byName.has(low)) {byName.set(low, new Set());}
    byName.get(low)?.add(streamNorm);
  }
  const counts = new Map<string, number>();
  for (const [low, streams] of byName) {
    counts.set(low, streams.size);
  }
  return counts;
}

/** Assign response column keys for a channel-values batch. */
export function assignStreamScopedOutputKeys(refs: ChannelRef[]): Array<ChannelRef & { output_key: string }> {
  const streamCounts = distinctNonemptyStreamCountByName(refs);
  return refs.map((ref) => {
    const needsDisambiguation = (streamCounts.get(normalizeChannelNameForMatch(ref.name)) ?? 0) > 1;
    const output_key = buildStreamScopedOutputKey(ref.name, ref.stream, needsDisambiguation);
    return { ...ref, output_key };
  });
}

export function parseAxisChannelRef(axis?: AxisChannelLike | null): ChannelRef | null {
  if (!axis?.name?.trim()) {return null;}
  const name = stripFleetChannelSuffix(axis.name.trim());
  if (!name) {return null;}
  const stream = normalizeStreamKey(axis.stream);
  const data_source = normalizeAxisDataSource(axis.data_source);
  return {
    name,
    ...(stream ? { stream } : {}),
    ...(data_source ? { data_source } : {}),
  };
}

const REF_DEDUPE = '\0';

export function channelRefKey(ref: Pick<ChannelRef, 'name' | 'stream'>): string {
  return `${normalizeChannelNameForMatch(ref.name)}${REF_DEDUPE}${normalizeStreamKey(ref.stream)}`;
}

/** Collect unique channel refs from chart series (x/y/color/z axes). */
export function collectChannelRefsFromCharts(charts: unknown[]): ChannelRef[] {
  const seen = new Set<string>();
  const out: ChannelRef[] = [];

  const addRef = (ref: ChannelRef | null) => {
    if (!ref?.name) {return;}
    const key = channelRefKey(ref);
    if (seen.has(key)) {return;}
    seen.add(key);
    out.push(ref);
  };

  for (const chart of charts || []) {
    const seriesList = (chart as { series?: unknown[] })?.series;
    if (!Array.isArray(seriesList)) {continue;}
    for (const series of seriesList) {
      const s = series as {
        xaxis?: AxisChannelLike;
        yaxis?: AxisChannelLike;
        colorChannel?: AxisChannelLike;
        zaxis?: AxisChannelLike;
      };
      addRef(parseAxisChannelRef(s.xaxis));
      addRef(parseAxisChannelRef(s.yaxis));
      addRef(parseAxisChannelRef(s.colorChannel));
      addRef(parseAxisChannelRef(s.zaxis));
    }
    const zTop = (chart as { zaxis?: AxisChannelLike }).zaxis;
    addRef(parseAxisChannelRef(zTop));
  }

  return out;
}

/** Key to read from flat channel-values row for this axis ref within the batch. */
export function resolveDataColumnKey(
  name: string,
  stream: string | null | undefined,
  batchRefs: ChannelRef[]
): string {
  const nameStripped = stripFleetChannelSuffix(name);
  const streamNorm = normalizeStreamKey(stream);
  const nameNorm = normalizeChannelNameForMatch(nameStripped);
  const withKeys = assignStreamScopedOutputKeys(batchRefs);
  for (const ref of withKeys) {
    if (normalizeChannelNameForMatch(ref.name) !== nameNorm) {continue;}
    if (normalizeStreamKey(ref.stream) !== streamNorm) {continue;}
    return ref.output_key;
  }
  const needsDisambiguation =
    (distinctNonemptyStreamCountByName(batchRefs).get(nameNorm) ?? 0) > 1;
  return buildStreamScopedOutputKey(nameStripped, stream, needsDisambiguation);
}

export function resolveAxisDataColumnKey(axis: AxisChannelLike | undefined | null, batchRefs: ChannelRef[]): string {
  if (!axis?.name?.trim()) {return '';}
  const name = stripFleetChannelSuffix(axis.name.trim());
  return resolveDataColumnKey(name, axis.stream, batchRefs);
}

export type ChannelListEntry = {
  name: string;
  type: string;
  stream?: string;
  output_key?: string;
  data_source?: ChannelRefDataSource;
};

/** Build API channel_list entries from refs (metadata channels added separately by caller). */
export function buildChannelListFromRefs(
  refs: ChannelRef[],
  defaultType = 'float'
): ChannelListEntry[] {
  return assignStreamScopedOutputKeys(refs).map((ref) => ({
    name: ref.name,
    type: defaultType,
    ...(ref.stream ? { stream: ref.stream } : {}),
    ...(ref.data_source ? { data_source: ref.data_source } : {}),
    ...(ref.output_key !== ref.name ? { output_key: ref.output_key } : {}),
  }));
}

/** Last two underscore segments (e.g. AC40_TWS_kts → tws_kts) for fleet vs short chart names. */
export function channelMatchSuffix(name: string): string {
  const norm = normalizeChannelNameForMatch(name);
  const parts = norm.split('_').filter(Boolean);
  if (parts.length >= 2) {return parts.slice(-2).join('_');}
  return norm;
}

function findRowKeyByChannelSuffix(
  row: Record<string, unknown>,
  fieldNorm: string
): string | undefined {
  const suffix = channelMatchSuffix(fieldNorm);
  if (!suffix) {return undefined;}
  const prefix = fieldNorm.split('_')[0] ?? '';
  const candidates = Object.keys(row).filter((k) => {
    const kn = normalizeChannelNameForMatch(k);
    if (!kn.endsWith(suffix) && !kn.endsWith(`_${suffix}`)) {return false;}
    return !prefix || kn.startsWith(prefix);
  });
  if (candidates.length === 1) {return candidates[0];}
  if (candidates.length > 1) {
    const exactTail = candidates.find((k) => normalizeChannelNameForMatch(k).endsWith(fieldNorm));
    if (exactTail) {return exactTail;}
    return candidates[0];
  }
  return undefined;
}

/** Case-insensitive row lookup (parquet vs chart config / stream-scoped output_key casing). */
export function getRowChannelValue(row: Record<string, unknown>, fieldName: string): unknown {
  if (!fieldName) {return undefined;}
  if (row[fieldName] !== undefined) {return row[fieldName];}
  const fieldNorm = normalizeChannelNameForMatch(fieldName);
  const key = Object.keys(row).find(
    (k) =>
      k.toLowerCase() === fieldName.toLowerCase() ||
      normalizeChannelNameForMatch(k) === fieldNorm
  );
  if (key !== undefined) {return row[key];}
  const suffixKey = findRowKeyByChannelSuffix(row, fieldNorm);
  if (suffixKey !== undefined) {return row[suffixKey];}
  return undefined;
}

/**
 * Resolve chart channel refs to catalog names/streams (dot vs underscore, stream correction).
 */
export function resolveChannelRefsAgainstCatalog(
  refs: ChannelRef[],
  catalog: CatalogChannelLike[]
): ChannelRef[] {
  return refs.map((ref) => {
    if (!ref?.name) {return ref;}

    const refStream = normalizeStreamKey(ref.stream);
    const refNorm = normalizeChannelNameForMatch(ref.name);

    let nameMatches = catalog.filter(
      (c) => c?.channel_name && normalizeChannelNameForMatch(c.channel_name) === refNorm
    );
    if (nameMatches.length === 0) {
      const suffix = channelMatchSuffix(refNorm);
      const prefix = refNorm.split('_')[0] ?? '';
      nameMatches = catalog.filter((c) => {
        if (!c?.channel_name) {return false;}
        const cn = normalizeChannelNameForMatch(c.channel_name);
        if (!cn.endsWith(suffix) && !cn.endsWith(`_${suffix}`)) {return false;}
        return !prefix || cn.startsWith(prefix);
      });
    }
    if (nameMatches.length === 0) {return ref;}

    let chosen: CatalogChannelLike | undefined;
    if (refStream) {
      chosen = nameMatches.find((c) => normalizeStreamKey(c.stream_name) === refStream);
    }
    if (!chosen && nameMatches.length === 1) {
      chosen = nameMatches[0];
    }
    if (!chosen) {return ref;}

    const catalogStream = normalizeStreamKey(chosen.stream_name);
    return {
      ...ref,
      name: chosen.channel_name,
      ...(catalogStream
        ? { stream: catalogStream }
        : ref.stream
          ? { stream: ref.stream }
          : {}),
    };
  });
}

export function parseParallelChannelRef(channel?: AxisChannelLike | null): ChannelRef | null {
  if (!channel?.name?.trim()) {return null;}
  const name = stripFleetChannelSuffix(channel.name.trim());
  if (!name) {return null;}
  const stream = normalizeStreamKey(channel.stream);
  const data_source = normalizeAxisDataSource(channel.data_source);
  return {
    name,
    ...(stream ? { stream } : {}),
    ...(data_source ? { data_source } : {}),
  };
}

/** Collect unique channel refs from parallel-coordinates chart series (series[].channel). */
export function collectChannelRefsFromParallelCharts(charts: unknown[]): ChannelRef[] {
  const seen = new Set<string>();
  const out: ChannelRef[] = [];

  const addRef = (ref: ChannelRef | null) => {
    if (!ref?.name) {return;}
    const key = channelRefKey(ref);
    if (seen.has(key)) {return;}
    seen.add(key);
    out.push(ref);
  };

  for (const chart of charts || []) {
    const seriesList = (chart as { series?: unknown[] })?.series;
    if (!Array.isArray(seriesList)) {continue;}
    for (const series of seriesList) {
      const channel = (series as { channel?: AxisChannelLike }).channel;
      addRef(parseParallelChannelRef(channel));
    }
  }

  return out;
}

/** Collect refs from parallel channels() signal entries ({ name, stream?, ... }). */
export function collectChannelRefsFromParallelChannels(
  channels: Array<{ name?: string; stream?: string; data_source?: string }>
): ChannelRef[] {
  const seen = new Set<string>();
  const out: ChannelRef[] = [];
  for (const ch of channels || []) {
    const ref = parseParallelChannelRef(ch);
    if (!ref?.name) {continue;}
    const key = channelRefKey(ref);
    if (seen.has(key)) {continue;}
    seen.add(key);
    out.push(ref);
  }
  return out;
}

/** Merge chart refs with extra string channel names (filters, Datetime, etc.) — no stream. */
export function mergeChannelRefsWithNames(refs: ChannelRef[], extraNames: string[]): ChannelRef[] {
  const seen = new Set(refs.map(channelRefKey));
  const merged = [...refs];
  for (const raw of extraNames) {
    const name = stripFleetChannelSuffix(String(raw || '').trim());
    if (!name) {continue;}
    const ref: ChannelRef = { name };
    const key = channelRefKey(ref);
    if (seen.has(key)) {continue;}
    seen.add(key);
    merged.push(ref);
  }
  return merged;
}
